// Libs
import clsx from "clsx";
import moment from "moment";

// Types
import {
    SERVICE_TIME_HOURS,
    TimeSlotSelectFilterChoiceEnum,
    type TimeSlotSelectFilterValue,
} from "./types";

// Utils
import { useTranslation } from "src/design-system/core/TranslationContext";
import {
    formatDurationToTime,
    formatStringTimeLt,
    formatTimeToDuration,
} from "src/utils/formatters";
import { buildHoursSlotString } from "src/utils/timeUtils";

// Components
import { RadioGroup, SelectField } from "src/design-system/molecules/inputs";
import { BaseDropdownFilter } from "src/design-system/organisms/filters/BaseDropdownFilter";

// TODO : OLD COMPONENTS IMPORTS
import { useCallback, useEffect } from "react";
import {
    FILTERS_COMPONENTS_TRANSLATION_NAMESPACE,
    TIMESLOT_FILTER_TK,
} from "src/components/Filters/config";
import { formatTimeSlotFilter } from "src/components/Filters/hooks";

interface TimeSlotSelectFilterProps {
    value: TimeSlotSelectFilterValue;
    disabled?: boolean;
    disableCustomSlot?: boolean;
    onChange: (value: TimeSlotSelectFilterValue) => void;
    onClose?: () => void;
}

const TIMEPICKER_MINUTES = [0, 30];
const TIMEPICKER_DEFAULT_OPEN_VALUE = moment.duration("12:00:00");
const TIMEPICKER_MIN_TIME = "05:00";
const TIMEPICKER_MAX_TIME = "29:00";

function getDefaultEndAt(startDuration: moment.Duration) {
    let endDuration = moment.duration(startDuration).add(2, "hours");
    if (endDuration.asHours() > SERVICE_TIME_HOURS.DINNER.end) {
        endDuration = moment.duration(SERVICE_TIME_HOURS.DINNER.end, "hours");
    }

    return endDuration;
}

export function TimeSlotSelectFilter({
    value,
    disabled = false,
    disableCustomSlot = false,
    onChange,
    onClose,
}: TimeSlotSelectFilterProps): JSX.Element {
    const { t: translateRaw } = useTranslation();
    const { t, translateCommon } = useTranslation(
        `${FILTERS_COMPONENTS_TRANSLATION_NAMESPACE}:${TIMESLOT_FILTER_TK}`
    );
    const hoursRange = buildHoursSlotString(
        translateCommon,
        TIMEPICKER_MIN_TIME,
        TIMEPICKER_MAX_TIME,
        TIMEPICKER_MINUTES
    );
    const {
        serviceTime: optionSelected,
        durationStartFromMidnight,
        durationEndFromMidnight,
    } = value;

    const handleServiceTimeChange = useCallback(
        (serviceTime: TimeSlotSelectFilterChoiceEnum) => {
            onChange({
                serviceTime: serviceTime,
            });
        },
        [onChange]
    );

    const handleCustomChange = () => {
        if (durationStartFromMidnight === undefined) {
            onChange({
                serviceTime: TimeSlotSelectFilterChoiceEnum.Custom,
                durationStartFromMidnight: TIMEPICKER_DEFAULT_OPEN_VALUE,
                durationEndFromMidnight: getDefaultEndAt(TIMEPICKER_DEFAULT_OPEN_VALUE),
            });
            return;
        }

        // DOC: We only need to change the serviceTime but spread the prev values to keep the signature of onChange correct
        // In our current implementation the onChange allows partial updates but the interface doesn't declare it so for clarity we spread all values
        onChange({
            ...value,
            serviceTime: TimeSlotSelectFilterChoiceEnum.Custom,
        });
    };

    const handleChoiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        switch (event.target.value) {
            case TimeSlotSelectFilterChoiceEnum.Custom:
                handleCustomChange();
                break;
            default:
                handleServiceTimeChange(event.target.value as TimeSlotSelectFilterChoiceEnum);
                break;
        }
    };

    const handleTimeChange = (
        field: "durationStartFromMidnight" | "durationEndFromMidnight",
        val: moment.Duration
    ) => {
        const start = field === "durationStartFromMidnight" ? val : durationStartFromMidnight;
        const end = field === "durationEndFromMidnight" ? val : durationEndFromMidnight;

        switch (field) {
            case "durationStartFromMidnight": {
                // If start is set and it is after the end (or end is unset), default end to start + 2h
                if (start && (!end || start > end)) {
                    onChange({
                        durationStartFromMidnight: start,
                        durationEndFromMidnight: getDefaultEndAt(start),
                    });
                    return;
                }

                onChange({
                    durationStartFromMidnight: start,
                });
                return;
            }

            case "durationEndFromMidnight": {
                // If the new end is before the current start, move start to end - 2h
                if (end && start && end < start) {
                    let newStart = moment.duration(end).subtract(2, "hours");
                    if (newStart.asHours() < SERVICE_TIME_HOURS.LUNCH.start) {
                        newStart = moment.duration(SERVICE_TIME_HOURS.LUNCH.start, "hours");
                    }
                    onChange({
                        durationEndFromMidnight: end,
                        durationStartFromMidnight: newStart,
                    });
                    return;
                }

                onChange({
                    durationEndFromMidnight: end,
                });
                return;
            }
        }
    };

    const hoursOptions = [
        {
            label: "-- : --",
            value: "",
        },
        ...hoursRange.map((e) => {
            return {
                label: formatStringTimeLt(e, translateCommon),
                value: e,
            };
        }),
    ];

    const optionRadioGroup = [
        {
            label: t("allDay"),
            value: TimeSlotSelectFilterChoiceEnum.AllDay,
        },
        {
            label: t("lunch"),
            subLabel: t("lunch.subLabel"),
            value: TimeSlotSelectFilterChoiceEnum.Lunch,
        },
        {
            label: t("dinner"),
            subLabel: t("dinner.subLabel"),
            value: TimeSlotSelectFilterChoiceEnum.Dinner,
        },
        ...(!disableCustomSlot
            ? [
                  {
                      label: t("custom"),
                      value: TimeSlotSelectFilterChoiceEnum.Custom,
                  },
              ]
            : []),
    ];

    useEffect(() => {
        if (disableCustomSlot && optionSelected === TimeSlotSelectFilterChoiceEnum.Custom) {
            handleServiceTimeChange(TimeSlotSelectFilterChoiceEnum.AllDay);
        }
    }, [disableCustomSlot, optionSelected, handleServiceTimeChange]);

    return (
        <BaseDropdownFilter
            disabled={disabled}
            isActive={value.serviceTime !== TimeSlotSelectFilterChoiceEnum.AllDay}
            name="time-select-drop-down"
            onClose={onClose}
            label={formatTimeSlotFilter(
                {
                    serviceTime: optionSelected,
                    durationStartFromMidnight: durationStartFromMidnight,
                    durationEndFromMidnight: durationEndFromMidnight,
                },
                translateRaw
            )}
            dropdownClassName="fl-c-time-slot-select-filter"
        >
            <div
                className={clsx(
                    "ds-o-flex",
                    "ds-o-flex--column",
                    "ds-o-flex--align-start",
                    "ds-o-flex--spaced",
                    "ds-o-flex-gap--XS" // lastChild padding 12px + 4px gap = 16px vertical padding
                )}
            >
                <div className={clsx("ds-o-flex", "ds-o-flex--spaced")}>
                    <RadioGroup
                        name="time-select-filter-radio-group"
                        onChange={handleChoiceChange}
                        options={optionRadioGroup}
                        value={optionSelected}
                    />
                </div>
                {!disableCustomSlot && (
                    <div className={clsx("ds-o-flex", "ds-o-flex--spaced")}>
                        <SelectField
                            required
                            inactive={optionSelected !== TimeSlotSelectFilterChoiceEnum.Custom}
                            onFocus={handleCustomChange}
                            name="time-slot-select-filter-start"
                            label={t("startAtHelper")}
                            id="time-slot-select-filter-start"
                            inputProps={{
                                size: "s",
                            }}
                            onChange={(event) => {
                                const value = event.target.value;
                                handleTimeChange(
                                    "durationStartFromMidnight",
                                    formatTimeToDuration(value, translateCommon)
                                );
                            }}
                            value={
                                optionSelected !== TimeSlotSelectFilterChoiceEnum.Custom
                                    ? ""
                                    : formatDurationToTime(
                                          durationStartFromMidnight,
                                          translateCommon
                                      )
                            }
                            options={hoursOptions}
                        />

                        <div
                            className={clsx({
                                "fl-c-time-slot-select-filter__to--inactive":
                                    optionSelected !== TimeSlotSelectFilterChoiceEnum.Custom,
                            })}
                        >
                            {t("to")}
                        </div>

                        <SelectField
                            required
                            inactive={optionSelected !== TimeSlotSelectFilterChoiceEnum.Custom}
                            onFocus={handleCustomChange}
                            name="time-slot-select-filter-start"
                            label={t("endAtHelper")}
                            id="time-slot-select-filter-start"
                            inputProps={{
                                size: "s",
                            }}
                            onChange={(event) => {
                                const value = event.target.value;
                                handleTimeChange(
                                    "durationEndFromMidnight",
                                    formatTimeToDuration(value, translateCommon)
                                );
                            }}
                            value={
                                optionSelected !== TimeSlotSelectFilterChoiceEnum.Custom
                                    ? ""
                                    : formatDurationToTime(durationEndFromMidnight, translateCommon)
                            }
                            options={hoursOptions}
                        />
                    </div>
                )}
            </div>
        </BaseDropdownFilter>
    );
}
